<template>
  <div>
    <div
      class="container mx-auto p-6 bg-white rounded-lg shadow-lg"
      v-if="!isLoaded"
    >
      <p>Yükleniyor...</p>
    </div>
    <div v-else>
      <!-- Fatura bilgisi yoksa -->
      <section
        class="container mx-auto p-6 bg-white rounded-lg shadow-lg"
        v-if="
          userInvoiceInformations.length === 0 &&
          !userWantToCreateNewInvoice &&
          !editMode
        "
      >
        <p>Henüz fatura bilgisi eklenmemiş.</p>
        <button
          @click="userWantToCreateNewInvoice = true"
          class="bg-blue-500 text-white px-4 py-2 rounded mt-4"
        >
          Yeni Fatura Bilgisi Ekle
        </button>
      </section>

      <!-- Fatura bilgileri listesi -->
      <section
        class="container mx-auto p-6 bg-white rounded-lg shadow-lg"
        v-if="
          userInvoiceInformations.length > 0 &&
          !userWantToCreateNewInvoice &&
          !editMode
        "
      >
        <div
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-4"
        >
          <div
            v-for="invoiceInfo in userInvoiceInformations"
            :key="invoiceInfo.Id"
            class="relative p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors duration-200 ease-in-out"
            :class="{
              'bg-blue-100': selectedInvoiceInformation === invoiceInfo,
            }"
          >
            <!-- Düzenle ve Silme butonları -->
            <div
              v-if="props.showEditDeleteButtons"
              class="absolute top-2 right-2 flex gap-2 z-10"
              style="right: 8px"
            >
              <button
                @click.stop="handleEdit(invoiceInfo)"
                class="text-blue-600 hover:text-blue-800"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button
                @click.stop="handleDelete(invoiceInfo)"
                class="text-red-600 hover:text-red-800"
              >
                <i class="fas fa-trash"></i>
              </button>
            </div>

            <div @click="selectInvoice(invoiceInfo)">
              <h2>
                <i class="fas fa-user"></i>
                {{
                  invoiceInfo.Type === InvoiceTypes.Individual
                    ? "Bireysel"
                    : "Kurumsal"
                }}
              </h2>
              <h2>
                <p>{{ invoiceInfo.FullName }}</p>
              </h2>
              <p v-if="invoiceInfo.CompanyName">
                {{ invoiceInfo.CompanyName }}
              </p>
              <p>{{ invoiceInfo.Address }}</p>
              <p>{{ invoiceInfo.TaxId }}</p>
              <p v-if="invoiceInfo.TaxOfficeId">
                {{ invoiceInfo.TaxOfficeId }}
              </p>
              <p>{{ invoiceInfo.Email }}</p>
              <span
                v-if="!showEditDeleteButtons"
                class="text-white bg-blue-500 px-2 py-1 w-full text-center block mt-4 cursor-pointer hover:bg-blue-600 transition-colors duration-200 ease-in-out rounded"
              >
                Seç
              </span>
            </div>
          </div>
        </div>

        <button
          @click="handleNewInvoice"
          class="bg-blue-500 text-white px-4 py-2 rounded mt-4"
        >
          Yeni Fatura Bilgisi Ekle
        </button>
      </section>

      <!-- Fatura bilgisi ekleme/düzenleme formu -->
      <section
        class="container mx-auto p-6 bg-white rounded-lg shadow-lg mt-6"
        v-if="userWantToCreateNewInvoice || editMode"
      >
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-xl font-semibold">
            {{
              editMode ? "Fatura Bilgisi Düzenle" : "Yeni Fatura Bilgisi Ekle"
            }}
          </h2>
          <button
            @click="handleCancel"
            class="text-gray-600 hover:text-gray-800"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Fatura Türü Seçimi -->
        <div class="mb-6">
          <label for="invoiceType" class="block font-medium mb-1"
            >Fatura Türü</label
          >
          <select
            v-model="invoiceType"
            id="invoiceType"
            class="w-full border border-gray-300 p-2 rounded"
          >
            <option
              v-for="(label, type) in invoiceTypeLabels"
              :key="type"
              :value="Number(type)"
            >
              {{ label }}
            </option>
          </select>
        </div>

        <!-- Ad Soyad -->
        <div class="mb-6">
          <label for="fullName" class="block font-medium mb-1">Ad Soyad</label>
          <input
            v-model="fullName"
            type="text"
            id="fullName"
            class="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        <!-- E-posta -->
        <div class="mb-6">
          <label for="email" class="block font-medium mb-1">E-posta</label>
          <input
            v-model="email"
            type="email"
            id="email"
            class="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        <!-- Telefon Numarası -->
        <div class="mb-6">
          <label for="phoneNumber" class="block font-medium mb-1"
            >Telefon Numarası</label
          >
          <input
            v-model="phoneNumber"
            type="tel"
            id="phoneNumber"
            class="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        <!-- Kurumsal veya Bireysel Alanlar -->
        <div v-if="invoiceType == InvoiceTypes.Corporation" class="space-y-6">
          <!-- Şirket Adı -->
          <div>
            <label for="companyName" class="block font-medium mb-1"
              >Şirket Adı</label
            >
            <input
              v-model="companyName"
              type="text"
              id="companyName"
              class="w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <!-- Vergi Numarası -->
          <div>
            <label for="taxId" class="block font-medium mb-1"
              >Vergi Numarası</label
            >
            <input
              v-model="taxId"
              type="number"
              id="taxId"
              class="w-full border border-gray-300 p-2 rounded"
            />
          </div>

          <!-- Vergi Dairesi -->
          <div>
            <label for="taxOfficeId" class="block font-medium mb-1"
              >Vergi Dairesi</label
            >
            <select
              v-model="taxOfficeId"
              id="taxOfficeId"
              class="w-full border border-gray-300 p-2 rounded"
            >
              <option
                v-for="taxOffice in taxOffices"
                :key="taxOffice.Id"
                :value="taxOffice.Id"
              >
                {{ taxOffice.Name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Bireysel Alanlar -->
        <div v-else>
          <!-- T.C. Kimlik Numarası -->
          <div>
            <label for="identityNumber" class="block font-medium mb-1"
              >T.C. Kimlik Numarası</label
            >
            <input
              v-model="taxId"
              type="number"
              id="identityNumber"
              class="w-full border border-gray-300 p-2 rounded"
            />
          </div>
        </div>

        <!-- Adres -->
        <div class="mt-6">
          <label for="address" class="block font-medium mb-1">Adres</label>
          <input
            v-model="address"
            type="text"
            id="address"
            class="w-full border border-gray-300 p-2 rounded"
          />
        </div>

        <!-- Sehir ve İlçe -->
        <div class="grid grid-cols-2 gap-4 mt-6">
          <div>
            <label for="city" class="block font-medium mb-1">Şehir</label>
            <select
              v-model="city"
              id="city"
              class="w-full border border-gray-300 p-2 rounded"
            >
              <option v-for="city in cities" :key="city.Id" :value="city.Id">
                {{ city.CityName }}
              </option>
            </select>
          </div>
          <div>
            <label for="county" class="block font-medium mb-1">İlçe</label>
            <select
              v-model="county"
              id="county"
              class="w-full border border-gray-300 p-2 rounded"
            >
              <option
                v-for="county in cityCounties"
                :key="county.Id"
                :value="county.Id"
              >
                {{ county.CountyName }}
              </option>
            </select>
          </div>
        </div>

        <div class="flex justify-end gap-4 mt-6">
          <button
            @click="handleCancel"
            class="bg-gray-500 text-white px-4 py-2 rounded"
          >
            İptal
          </button>
          <button
            @click="handleSubmit"
            class="bg-blue-500 text-white px-4 py-2 rounded"
          >
            {{ editMode ? "Güncelle" : "Kaydet" }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, defineProps, defineEmits } from "vue";
import { Toast } from "@/services/toastService";
import {
  addUserInvoiceInformation,
  updateUserInvoiceInformation,
  fetchAllUserInvoiceInformations,
  getCityCounties,
  getTaxOffices,
  deleteUserInvoiceInformation,
} from "@/services/userServices";
import {
  isValidPhone,
  isValidEmail,
  phoneNumberFormatter,
  isValidTaxId,
} from "@/services/common";
import { CommonDefinitions } from "@/auxiliary/List";

const props = defineProps({
  showEditDeleteButtons: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "invoiceSelected",
  "invoiceAdded",
  "invoiceUpdated",
  "invoiceDeleted",
]);

// Form fields
const fullName = ref("");
const address = ref("");
const email = ref("");
const phoneNumber = ref("");
const taxId = ref(null);
const taxOfficeId = ref(null);
const cities = ref(CommonDefinitions.Cities);
const cityCounties = ref([]);
const city = ref(null);
const county = ref(null);
const userInvoiceInformations = ref([]);
const isLoaded = ref(false);
const userWantToCreateNewInvoice = ref(false);
const companyName = ref("");
const taxOffices = ref([]);

// on city change make sure to reset county
watch(city, () => {
  county.value = null;
});

// Invoice types enum
const InvoiceTypes = Object.freeze({
  Individual: 0,
  Corporation: 1,
});

const invoiceType = ref(InvoiceTypes.Individual);
const selectedInvoiceInformation = ref(null);
const editMode = ref(false);
const editingInvoiceId = ref(null);

const invoiceTypeLabels = {
  [InvoiceTypes.Individual]: "Bireysel",
  [InvoiceTypes.Corporation]: "Kurumsal",
};

// Watch city selection to load counties
watch(city, async (newCityId) => {
  if (newCityId) {
    cityCounties.value = await getCityCounties(newCityId);
  }
});

// Watch invoice type to load tax offices
watch(invoiceType, async (newInvoiceType) => {
  if (newInvoiceType == InvoiceTypes.Corporation && !taxOffices.value.length) {
    taxOffices.value = await getTaxOffices();
  }
});

// Format phone number
watch(phoneNumber, (newPhoneNumber) => {
  phoneNumber.value = phoneNumberFormatter(newPhoneNumber);
});

// Validation function
const validateFields = () => {
  if (invoiceType.value == InvoiceTypes.Corporation) {
    // Corporate validation
    if (!fullName.value || !fullName.value.includes(" ")) {
      Toast(
        "Hata",
        "Ad Soyad alanı boş bırakılamaz ve Ad Soyad alanı tam adınızı içermelidir.",
        "error"
      );
      return false;
    }
    if (!email.value || !isValidEmail(email.value)) {
      Toast(
        "Hata",
        "E-posta alanı boş bırakılamaz ve E-posta alanı geçerli bir e-posta adresi içermelidir.",
        "error"
      );
      return false;
    }
    if (!phoneNumber.value || !isValidPhone(phoneNumber.value)) {
      Toast(
        "Hata",
        "Telefon Numarası alanı boş bırakılamaz ve Telefon Numarası alanı en az 10 karakter içermelidir.",
        "error"
      );
      return false;
    }
    if (!companyName.value) {
      Toast("Hata", "Şirket Adı alanı boş bırakılamaz.", "error");
      return false;
    }
    if (!taxId.value || !isValidTaxId(taxId.value, true)) {
      Toast(
        "Hata",
        "Vergi Numarası alanı boş bırakılamaz ve 10 Haneli olmalidir.",
        "error"
      );
      return false;
    }
    if (!taxOfficeId.value) {
      Toast("Hata", "Vergi Dairesi alanı boş bırakılamaz.", "error");
      return false;
    }
  } else {
    // Individual validation
    if (!fullName.value || !fullName.value.includes(" ")) {
      Toast(
        "Hata",
        "Ad Soyad alanı boş bırakılamaz ve Ad Soyad alanı tam adınızı içermelidir.",
        "error"
      );
      return false;
    }
    if (!email.value || !isValidEmail(email.value)) {
      Toast(
        "Hata",
        "E-posta alanı boş bırakılamaz ve E-posta alanı geçerli bir e-posta adresi içermelidir.",
        "error"
      );
      return false;
    }
    if (!phoneNumber.value || !isValidPhone(phoneNumber.value)) {
      Toast(
        "Hata",
        "Telefon Numarası alanı boş bırakılamaz ve Telefon Numarası alanı en az 10 karakter içermelidir.",
        "error"
      );
      return false;
    }
    if (!taxId.value || !isValidTaxId(taxId.value, false)) {
      Toast(
        "Hata",
        "T.C. Kimlik Numarası alanı boş bırakılamaz. ve 11 karakter olmalıdır.",
        "error"
      );
      return false;
    }
  }

  // Common validation for both types
  if (!address.value) {
    Toast("Hata", "Adres alanı boş bırakılamaz.", "error");
    return false;
  }
  if (!city.value) {
    Toast("Hata", "Şehir alanı boş bırakılamaz.", "error");
    return false;
  }
  if (!county.value) {
    Toast("Hata", "İlçe alanı boş bırakılamaz.", "error");
    return false;
  }

  return true;
};

// Event handlers
const handleEdit = (invoice) => {
  editMode.value = true;
  editingInvoiceId.value = invoice.Id;

  // Fill form with invoice data
  fullName.value = invoice.FullName;
  address.value = invoice.Address;
  email.value = invoice.Email;
  phoneNumber.value = invoice.PhoneNumber;
  taxId.value = invoice.TaxId;
  taxOfficeId.value = invoice.TaxOfficeId;
  companyName.value = invoice.CompanyName || "";
  city.value = invoice.CityId;
  invoiceType.value = invoice.Type;

  // Load counties for the selected city
  if (invoice.CityId) {
    getCityCounties(invoice.CityId).then((counties) => {
      cityCounties.value = counties;
      county.value = invoice.CountyId;
    });
  }
};

const handleDelete = (invoice) => {
  Toast(
    "Emin misiniz?",
    "Bu fatura bilgisini silmek istediğinizden emin misiniz?",
    "warning",
    async () => {
      isLoaded.value = false;
      try {
        const response = await deleteUserInvoiceInformation(invoice.Id);
        if (response.Success) {
          Toast("Başarılı", "Fatura bilgisi silindi.", "success");
          userInvoiceInformations.value =
            await fetchAllUserInvoiceInformations();
          emit("invoiceDeleted", invoice.Id);
        } else {
          Toast("Hata", response.Message, "error");
        }
      } catch (error) {
        Toast("Hata", "Silme işlemi sırasında bir hata oluştu.", "error");
      } finally {
        isLoaded.value = true;
      }
    },
    null,
    true
  );
};

const handleNewInvoice = () => {
  resetForm();
  userWantToCreateNewInvoice.value = true;
};

const handleCancel = () => {
  resetForm();
  editMode.value = false;
  userWantToCreateNewInvoice.value = false;
};

const resetForm = () => {
  fullName.value = "";
  address.value = "";
  email.value = "";
  phoneNumber.value = "";
  taxId.value = null;
  taxOfficeId.value = null;
  companyName.value = "";
  city.value = null;
  county.value = null;
  invoiceType.value = InvoiceTypes.Individual;
  editingInvoiceId.value = null;
};

const handleSubmit = async () => {
  if (!validateFields()) return;

  const invoiceData = {
    Id: editingInvoiceId.value,
    address: address.value,
    fullName: fullName.value,
    type: invoiceType.value,
    companyName: companyName.value || "",
    phoneNumber: phoneNumber.value,
    taxId: taxId.value,
    taxOfficeId: taxOfficeId.value,
    email: email.value,
    cityId: city.value,
    countyId: county.value,
  };

  isLoaded.value = false;

  try {
    let response;
    if (editMode.value) {
      response = await updateUserInvoiceInformation(invoiceData);
      if (response.Success) {
        emit("invoiceUpdated", { ...invoiceData, Id: editingInvoiceId.value });
      }
    } else {
      response = await addUserInvoiceInformation(invoiceData);
      if (response.Success) {
        emit("invoiceAdded", response.Data);
      }
    }

    if (response.Success) {
      Toast(
        "Başarılı",
        editMode.value
          ? "Fatura bilgisi güncellendi."
          : "Fatura bilgisi başarıyla eklendi.",
        "success"
      );
      userInvoiceInformations.value = await fetchAllUserInvoiceInformations();
      handleCancel();
    } else {
      Toast("Hata", response.Message, "error");
    }
  } catch (error) {
    Toast("Hata", "İşlem sırasında bir hata oluştu.", "error");
  } finally {
    isLoaded.value = true;
  }
};

const selectInvoice = (invoice) => {
  selectedInvoiceInformation.value = invoice;
  emit("invoiceSelected", invoice);
};

onMounted(async () => {
  userInvoiceInformations.value = await fetchAllUserInvoiceInformations();
  isLoaded.value = true;
});
</script>
